import React, { useState } from 'react';
import { Calendar, School, Clock, Settings, Save, X, ArrowLeft, Plus, Minus } from 'lucide-react';
import '../styles/Planner.css';

const Planner = ({ onNext }) => {
  const [formData, setFormData] = useState({
    // School Information
    nameEnglish: 'Lui Kee No. 1 Secondary School',
    nameChinese: '呂祺第一中學',
    year: '1988',
    level: '3',
    session: '3',
    
    // Timetable Settings
    cycleWeek: 'Cycle', // Cycle or Week
    dayCycle: 6,
    specialDay: 'No', // Yes or No
    specialDayOn: '',
    specialDayPeriods: '',
    maxPeriodsPerDay: 8,
    defaultMaxLoadPerDay: 6
  });

  const [mode, setMode] = useState('view'); // view, modify

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleModify = () => {
    setMode('modify');
  };

  const handleCancel = () => {
    setMode('view');
  };

  const handleSave = () => {
    // Save logic here
    setMode('view');
    console.log('Saving form data:', formData);
  };

  const handleClose = () => {
    // Close logic here
    console.log('Closing planner');
  };

  const handleNextPage = () => {
    if (onNext) {
      onNext('settings');
    }
  };

  const incrementValue = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: Math.min(prev[field] + 1, 99)
    }));
  };

  const decrementValue = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: Math.max(prev[field] - 1, 0)
    }));
  };

  return (
    <div className="planner-container">
      {/* Header */}
      <div className="planner-header">
        <div className="planner-header-content">
          <div className="planner-icon">
            <School className="icon" />
          </div>
          <div className="planner-title-section">
            <h1 className="planner-title">School (TBM0110E)</h1>
            <p className="planner-subtitle">學校資料/時間表設定 (School Data/Timetable Setup)</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="planner-content">
        {/* School Information Section */}
        <div className="form-section">
          <div className="form-section-header">
            <h3 className="section-title">學校資料 (School Data)</h3>
          </div>
          
          <div className="form-grid">
            {/* Name (English) */}
            <div className="form-group">
              <label className="form-label">Name (English):</label>
              <input
                type="text"
                value={formData.nameEnglish}
                onChange={(e) => handleInputChange('nameEnglish', e.target.value)}
                disabled={mode === 'view'}
                className="form-input"
              />
            </div>

            {/* Name (Chinese) */}
            <div className="form-group">
              <label className="form-label">Name (Chinese):</label>
              <input
                type="text"
                value={formData.nameChinese}
                onChange={(e) => handleInputChange('nameChinese', e.target.value)}
                disabled={mode === 'view'}
                className="form-input"
              />
            </div>

            {/* Year */}
            <div className="form-group">
              <label className="form-label">Year:</label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => handleInputChange('year', e.target.value)}
                disabled={mode === 'view'}
                className="form-input"
              />
            </div>

            {/* Level */}
            <div className="form-group">
              <label className="form-label">Level:</label>
              <input
                type="number"
                value={formData.level}
                onChange={(e) => handleInputChange('level', e.target.value)}
                disabled={mode === 'view'}
                className="form-input"
              />
            </div>

            {/* Session */}
            <div className="form-group">
              <label className="form-label">Session:</label>
              <input
                type="number"
                value={formData.session}
                onChange={(e) => handleInputChange('session', e.target.value)}
                disabled={mode === 'view'}
                className="form-input"
              />
            </div>
          </div>
        </div>

        {/* Timetable Settings Section */}
        <div className="form-section">
          <div className="form-section-header">
            <h3 className="section-title">時間表設定 (Timetable Settings)</h3>
          </div>
          
          <div className="form-grid">
            {/* Cycle/Week */}
            <div className="form-group">
              <label className="form-label">Cycle/Week:</label>
              <div className="radio-group">
                <label className="radio-option">
                  <input
                    type="radio"
                    name="cycleWeek"
                    value="Cycle"
                    checked={formData.cycleWeek === 'Cycle'}
                    onChange={(e) => handleInputChange('cycleWeek', e.target.value)}
                    disabled={mode === 'view'}
                    className="radio-input"
                  />
                  <span className="radio-label">Cycle</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="cycleWeek"
                    value="Week"
                    checked={formData.cycleWeek === 'Week'}
                    onChange={(e) => handleInputChange('cycleWeek', e.target.value)}
                    disabled={mode === 'view'}
                    className="radio-input"
                  />
                  <span className="radio-label">Week</span>
                </label>
              </div>
            </div>

            {/* #Day/Cycle */}
            <div className="form-group">
              <label className="form-label">#Day/Cycle:</label>
              <div className="number-input-group">
                <button
                  type="button"
                  onClick={() => decrementValue('dayCycle')}
                  disabled={mode === 'view'}
                  className="number-btn"
                >
                  <Minus size={14} />
                </button>
                <input
                  type="number"
                  value={formData.dayCycle}
                  onChange={(e) => handleInputChange('dayCycle', parseInt(e.target.value) || 0)}
                  disabled={mode === 'view'}
                  className="number-input"
                />
                <button
                  type="button"
                  onClick={() => incrementValue('dayCycle')}
                  disabled={mode === 'view'}
                  className="number-btn"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>

            {/* Special Day */}
            <div className="form-group">
              <label className="form-label">Special Day?:</label>
              <div className="radio-group">
                <label className="radio-option">
                  <input
                    type="radio"
                    name="specialDay"
                    value="No"
                    checked={formData.specialDay === 'No'}
                    onChange={(e) => handleInputChange('specialDay', e.target.value)}
                    disabled={mode === 'view'}
                    className="radio-input"
                  />
                  <span className="radio-label">No</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="specialDay"
                    value="Yes"
                    checked={formData.specialDay === 'Yes'}
                    onChange={(e) => handleInputChange('specialDay', e.target.value)}
                    disabled={mode === 'view'}
                    className="radio-input"
                  />
                  <span className="radio-label">Yes</span>
                </label>
              </div>
            </div>

            {/* on Day */}
            <div className="form-group">
              <label className="form-label">on Day:</label>
              <input
                type="text"
                value={formData.specialDayOn}
                onChange={(e) => handleInputChange('specialDayOn', e.target.value)}
                disabled={mode === 'view'}
                className="form-input"
                placeholder="Enter day"
              />
            </div>

            {/* Periods of Special Day as */}
            <div className="form-group">
              <label className="form-label">Periods of Special Day as:</label>
              <div className="periods-input-group">
                <input
                  type="number"
                  value={formData.specialDayPeriods}
                  onChange={(e) => handleInputChange('specialDayPeriods', e.target.value)}
                  disabled={mode === 'view'}
                  className="form-input"
                />
                <span className="periods-label">Periods</span>
              </div>
            </div>

            {/* Maximum #Periods/Day */}
            <div className="form-group">
              <label className="form-label">Maximum #Periods/Day:</label>
              <div className="number-input-group">
                <button
                  type="button"
                  onClick={() => decrementValue('maxPeriodsPerDay')}
                  disabled={mode === 'view'}
                  className="number-btn"
                >
                  <Minus size={14} />
                </button>
                <input
                  type="number"
                  value={formData.maxPeriodsPerDay}
                  onChange={(e) => handleInputChange('maxPeriodsPerDay', parseInt(e.target.value) || 0)}
                  disabled={mode === 'view'}
                  className="number-input"
                />
                <button
                  type="button"
                  onClick={() => incrementValue('maxPeriodsPerDay')}
                  disabled={mode === 'view'}
                  className="number-btn"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>

            {/* Default Max Load/Day */}
            <div className="form-group">
              <label className="form-label">Default Max Load/Day:</label>
              <div className="number-input-group">
                <button
                  type="button"
                  onClick={() => decrementValue('defaultMaxLoadPerDay')}
                  disabled={mode === 'view'}
                  className="number-btn"
                >
                  <Minus size={14} />
                </button>
                <input
                  type="number"
                  value={formData.defaultMaxLoadPerDay}
                  onChange={(e) => handleInputChange('defaultMaxLoadPerDay', parseInt(e.target.value) || 0)}
                  disabled={mode === 'view'}
                  className="number-input"
                />
                <button
                  type="button"
                  onClick={() => incrementValue('defaultMaxLoadPerDay')}
                  disabled={mode === 'view'}
                  className="number-btn"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Special Day Information */}
        <div className="info-section">
          <div className="info-icon">
            <Clock className="icon" />
          </div>
          <div className="info-content">
            <h4 className="info-title">特別日設定說明</h4>
            <p className="info-text">
              特別日（Special Day）的安排。因為列印時間表時，特別日的時間會列印於時間表的最右邊而並非列印於特別日旁。
              所以，特別日是特別適用於長短舊制的學校（編排星期六的時間表）。
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="planner-actions">
        <button
          onClick={handleClose}
          className="action-btn close-btn"
        >
          <X size={16} />
          關閉 (Close)
        </button>
        
        <button
          onClick={handleNextPage}
          className="action-btn next-btn"
        >
          <ArrowLeft size={16} />
          下一步 (Next Page)
        </button>
        
        <button
          onClick={handleModify}
          disabled={mode === 'modify'}
          className="action-btn modify-btn"
        >
          <Settings size={16} />
          編修 (Modify)
        </button>
        
        <button
          onClick={handleCancel}
          disabled={mode === 'view'}
          className="action-btn cancel-btn"
        >
          <X size={16} />
          取消 (Cancel)
        </button>
        
        <button
          onClick={handleSave}
          disabled={mode === 'view'}
          className="action-btn save-btn"
        >
          <Save size={16} />
          儲存 (Save)
        </button>
      </div>
    </div>
  );
};

export default Planner;